import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  User, 
  Key, 
  Shield,
  Clock,
  Filter,
  Search
} from 'lucide-react';
import { Input } from '../ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs`);
      setAuditLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
      case 'update_status':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getResourceIcon = (resource) => {
    switch (resource.toLowerCase()) {
      case 'user':
        return User;
      case 'api_key':
        return Key;
      default:
        return Shield;
    }
  };

  const formatAction = (action) => {
    return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const filteredLogs = auditLogs.filter(log => 
    log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
    JSON.stringify(log.details).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout title="Audit Logs">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Audit Logs">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">System Activity</h2>
            <p className="text-sm text-gray-600">
              Track all administrative actions and system changes
            </p>
          </div>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {auditLogs.length} Events
          </Badge>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search audit logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Audit Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Activity Timeline
          </CardTitle>
          <CardDescription>
            Chronological record of all system activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredLogs.length > 0 ? (
            <div className="space-y-4">
              {filteredLogs.map((log) => {
                const ResourceIcon = getResourceIcon(log.resource);
                
                return (
                  <div key={log.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border-2 border-gray-200">
                        <ResourceIcon className="h-5 w-5 text-gray-600" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Badge className={getActionColor(log.action)}>
                            {formatAction(log.action)}
                          </Badge>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {log.resource}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-1" />
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600 mb-2">
                        <strong>User ID:</strong> {log.user_id}
                      </div>
                      
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-sm">
                          <strong className="text-gray-700">Details:</strong>
                          <div className="mt-1 p-3 bg-white rounded border font-mono text-xs">
                            {Object.entries(log.details).map(([key, value]) => (
                              <div key={key} className="flex">
                                <span className="text-gray-600 w-24">{key}:</span>
                                <span className="text-gray-900 flex-1">
                                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              {searchTerm ? (
                <>
                  <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
                  <p className="text-gray-600">
                    No audit logs match your search criteria
                  </p>
                </>
              ) : (
                <>
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Audit Logs</h3>
                  <p className="text-gray-600">
                    System activities will appear here once users start performing actions
                  </p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {auditLogs.length > 0 && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-gray-900">
              {auditLogs.filter(log => log.action === 'CREATE').length}
            </div>
            <div className="text-sm text-gray-600">Create Actions</div>
          </Card>
          
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-gray-900">
              {auditLogs.filter(log => log.action.includes('UPDATE')).length}
            </div>
            <div className="text-sm text-gray-600">Update Actions</div>
          </Card>
          
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-gray-900">
              {auditLogs.filter(log => log.action === 'DELETE').length}
            </div>
            <div className="text-sm text-gray-600">Delete Actions</div>
          </Card>
          
          <Card className="text-center p-4">
            <div className="text-2xl font-bold text-gray-900">
              {new Set(auditLogs.map(log => log.user_id)).size}
            </div>
            <div className="text-sm text-gray-600">Active Admins</div>
          </Card>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAuditLogs;